import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { rawFetch } from "@/lib/api/client";
import { useWorkspacePermissions } from "@/lib/workspacePermissions";
import { TermSetSelector } from "@/components/TermSetSelector";
import { useRetrievalReadiness } from "@/components/RetrievalReadiness";
import { Checkbox, Radio } from "@/components/ui-kit";

type ModeKey = "term_definitions" | "bm25" | "vector" | "graph";
type Mode = { key: string; weight?: number; config?: Record<string, unknown> };
type Policy = { key: string; [key: string]: unknown };
type Profile = {
  id: string; name: string; description: string; modes: Mode[]; policies: Policy[];
  top_k: number; freshness: string; rerank: Record<string, unknown>;
};
type Page<T> = { results: T[] };
type Choice = { id: string; name?: string; semver?: string; status?: string; connector_key?: string };
type CredentialChoice = { id: string; provider: string; model: string; label: string; alias?: string;
  kind: string; enabled: boolean };
type Capability = { enabled: boolean; count: number; reason: string };
type Capabilities = Record<string, Capability>;

const methods: Array<{ key: ModeKey; label: string }> = [
  { key: "term_definitions", label: "Approved terms" }, { key: "bm25", label: "Document wording" },
  { key: "vector", label: "Document meaning" }, { key: "graph", label: "Relationships" },
];
const presets: Record<string, ModeKey[]> = {
  "Glossary lookup": ["term_definitions"], "Document Q&A": ["bm25", "vector"],
  Relationships: ["graph", "bm25"], Balanced: ["term_definitions", "bm25", "vector", "graph"],
};
const presetDescriptions: Record<string, string> = {
  "Glossary lookup": "Definitions from approved terms, without indexing documents.",
  "Document Q&A": "Find evidence in indexed files by wording and meaning.",
  Relationships: "Follow known links between entities and documents.",
  Balanced: "Combine available terms, documents and relationships.",
};
const metadataKeys = ["lob", "product", "business_unit", "jurisdiction", "language", "data_entity"];
const emptyWeights: Record<ModeKey, number> = { term_definitions: 1, bm25: 1, vector: 1, graph: 1 };

export function parseLocaleDecimal(value: string): number | null {
  const normalized = value.trim().replace(",", ".");
  if (!/^\d+(?:\.\d+)?$/.test(normalized)) return null;
  const number = Number(normalized);
  return Number.isFinite(number) ? number : null;
}

function DecimalInput({ value, onChange, min, max, label, disabled }: {
  value: number; onChange: (value: number) => void; min: number; max: number;
  label: string; disabled?: boolean;
}) {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => setDraft(String(value)), [value]);
  return <input type="text" inputMode="decimal" className="input ml-1 w-20"
    aria-label={label} title={`${min}–${max}; comma or dot decimals accepted`}
    value={draft} disabled={disabled} onChange={(event) => setDraft(event.target.value)}
    onBlur={() => { const parsed = parseLocaleDecimal(draft);
      if (parsed !== null && parsed >= min && parsed <= max) onChange(parsed);
      else setDraft(String(value)); }} />;
}

export function RetrievalProfileBuilder({ workspace, onSelect, selectedProfileId }: {
  workspace: string | null; onSelect: (id: string) => void; selectedProfileId?: string;
}) {
  const qc = useQueryClient();
  const { canEdit } = useWorkspacePermissions();
  const suffix = workspace ? `?workspace=${encodeURIComponent(workspace)}` : "";
  const profiles = useQuery<Page<Profile>>({ queryKey: ["profiles", workspace],
    queryFn: () => rawFetch(`/retrieval/profiles/${suffix}&page_size=100`.replace("?&", "?")),
    enabled: Boolean(workspace) });
  const caps = useQuery<Capabilities>({ queryKey: ["retrieval-capabilities", workspace],
    queryFn: () => rawFetch(`/retrieval/profiles/capabilities/${suffix}`), enabled: Boolean(workspace) });
  const sources = useQuery<Page<Choice>>({ queryKey: ["retrieval-sources", workspace],
    queryFn: () => rawFetch(`/catalog/sources/${suffix}&page_size=100`.replace("?&", "?")), enabled: Boolean(workspace) });
  const versions = useQuery<Page<Choice>>({ queryKey: ["retrieval-versions", workspace],
    queryFn: () => rawFetch(`/ontology/versions/${suffix}&page_size=100`.replace("?&", "?")), enabled: Boolean(workspace) });
  const credentialQuery = useQuery<Page<CredentialChoice> | CredentialChoice[]>({
    queryKey: ["credentials", workspace],
    queryFn: () => rawFetch(`/iam/credentials/${suffix}&page_size=100`.replace("?&", "?")),
    enabled: Boolean(workspace),
  });
  const llmCredentials = (Array.isArray(credentialQuery.data) ? credentialQuery.data : credentialQuery.data?.results ?? [])
    .filter((row) => row.kind === "llm" && row.enabled);

  const [activeId, setActiveId] = useState(() => new URLSearchParams(window.location.search).get("profile") || "");
  useEffect(() => { if (selectedProfileId !== undefined) setActiveId(selectedProfileId); }, [selectedProfileId]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [routing, setRouting] = useState<"fixed" | "auto">("fixed");
  const [selected, setSelected] = useState<ModeKey[]>(["bm25"]);
  const [weights, setWeights] = useState(emptyWeights);
  const [hops, setHops] = useState(2);
  const [sourceIds, setSourceIds] = useState<string[]>([]);
  const [termSetIds, setTermSetIds] = useState<string[]>([]);
  const [ontologyId, setOntologyId] = useState(() => new URLSearchParams(window.location.search).get("ontology") || "");
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [previewFilters, setPreviewFilters] = useState<Record<string, string>>({});
  const [recencyDays, setRecencyDays] = useState(0);
  const [boostValues, setBoostValues] = useState<Record<string, string>>({});
  const [boostWeights, setBoostWeights] = useState<Record<string, number>>({});
  const [sourceTrust, setSourceTrust] = useState<Record<string, number>>({});
  const [rerankCredentialId, setRerankCredentialId] = useState("");
  const [topK, setTopK] = useState(10);
  const [live, setLive] = useState(false);
  const [liveSourceId, setLiveSourceId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const seededWorkspace = useRef("");
  useEffect(() => { const timer = window.setTimeout(() => setPreviewFilters(filters), 300);
    return () => window.clearTimeout(timer); }, [filters]);
  const readiness = useRetrievalReadiness(activeId, previewFilters);

  useEffect(() => {
    if (!workspace || !canEdit || !profiles.data || seededWorkspace.current === workspace) return;
    seededWorkspace.current = workspace;
    if (profiles.data.results.length) return;
    rawFetch<{ profile: Profile }>(`/retrieval/profiles/ensure-default/?workspace=${encodeURIComponent(workspace)}`, {
      method: "POST", body: JSON.stringify({ workspace }),
    }).then(async (result) => {
      await qc.invalidateQueries({ queryKey: ["profiles", workspace] });
      setActiveId(result.profile.id); onSelect(result.profile.id);
    }).catch(() => {
      seededWorkspace.current = "";
      setError("Could not seed the workspace default profile. Use the button to retry.");
    });
  }, [workspace, canEdit, profiles.data, qc, onSelect]);

  useEffect(() => {
    const row = profiles.data?.results.find((profile) => profile.id === activeId);
    if (!row) return;
    const scope: Policy = row.policies.find((policy) => policy.key === "scope") || { key: "scope" };
    const ranking: Policy = row.policies.find((policy) => policy.key === "ranking") || { key: "ranking" };
    const boosts = Array.isArray(ranking.metadata_boosts) ? ranking.metadata_boosts as Array<{ key: string; value: string; weight: number }> : [];
    const keys = row.modes.map((mode) => mode.key);
    setName(row.name); setDescription(row.description || "");
    setRouting(keys.includes("auto") ? "auto" : "fixed");
    setSelected(keys.filter((key): key is ModeKey => methods.some((method) => method.key === key)));
    setWeights({ ...emptyWeights, ...Object.fromEntries(row.modes.map((mode) => [mode.key, mode.weight || 1])) });
    setHops(Number(row.modes.find((mode) => mode.key === "graph")?.config?.hops || 2));
    setSourceIds(Array.isArray(scope.source_ids) ? scope.source_ids as string[] : []);
    setTermSetIds(Array.isArray(scope.term_set_version_ids) ? scope.term_set_version_ids as string[] : []);
    setOntologyId(typeof scope.ontology_version_id === "string" ? scope.ontology_version_id : "");
    setFilters(typeof scope.metadata_filters === "object" && scope.metadata_filters ? scope.metadata_filters as Record<string, string> : {});
    setRecencyDays(Number(ranking.recency_half_life_days || 0));
    setBoostValues(Object.fromEntries(boosts.map((boost) => [boost.key, boost.value])));
    setBoostWeights(Object.fromEntries(boosts.map((boost) => [boost.key, boost.weight])));
    setSourceTrust(typeof ranking.source_trust_weights === "object" && ranking.source_trust_weights ? ranking.source_trust_weights as Record<string, number> : {});
    setRerankCredentialId(row.rerank?.key === "workspace_llm" ? String(row.rerank.credential_id || "") : "");
    setTopK(row.top_k || 10); setLive(row.modes.some((mode) => mode.key === "mcp.live"));
    setLiveSourceId(String(row.modes.find((mode) => mode.key === "mcp.live")?.config?.datasource_id || ""));
  }, [activeId, profiles.data]);

  const invalidate = async () => {
    await qc.invalidateQueries({ queryKey: ["profiles", workspace] });
    await qc.invalidateQueries({ queryKey: ["table", "/retrieval/profiles/"] });
  };
  const reset = () => { setActiveId(""); setName(""); setDescription(""); setRouting("fixed");
    setSelected(["bm25"]); setWeights(emptyWeights); setSourceIds([]); setTermSetIds([]);
    setOntologyId(""); setFilters({}); setRecencyDays(0); setBoostValues({}); setBoostWeights({}); setSourceTrust({}); setRerankCredentialId("");
    setTopK(10); setLive(false); setLiveSourceId(""); setError(""); setNotice(""); };
  const available = (key: ModeKey) => key === "term_definitions"
    ? Boolean(ontologyId) : Boolean(caps.data?.[key]?.enabled);
  const reason = (key: ModeKey) => key === "term_definitions"
    ? "Choose an ontology version" : caps.data?.[key]?.reason || "Unavailable";
  const recommendedPreset = caps.data?.graph?.enabled && caps.data?.bm25?.enabled
    ? "Balanced" : caps.data?.bm25?.enabled ? "Document Q&A" : "Glossary lookup";
  const wordingPreference = weights.bm25 > weights.vector ? "wording"
    : weights.vector > weights.bm25 ? "meaning" : "equal";
  const recencyChoice = recencyDays === 0 ? "off" : recencyDays === 180 ? "somewhat"
    : recencyDays === 30 ? "strongly" : "custom";

  async function save() {
    if (!workspace || !canEdit) return;
    setBusy(true); setError(""); setNotice("");
    try {
      const modes: Mode[] = routing === "auto" ? [{ key: "auto" }] : selected.map((key) => ({
        key, weight: weights[key], config: key === "graph" ? { hops }
          : key === "term_definitions" ? { ontology_version_id: ontologyId } : {},
      }));
      if (live) {
        if (!liveSourceId) throw new Error("Choose an active MCP source for live retrieval.");
        modes.push({ key: "mcp.live", weight: 1,
          config: { datasource_id: liveSourceId, tool: "search" } });
      }
      if (!name.trim() || !modes.length) throw new Error("Choose a name and at least one method.");
      const payload = { name: name.trim(), description, modes, top_k: topK,
        freshness: live ? "hybrid" : "indexed",
        rerank: rerankCredentialId ? { key: "workspace_llm", credential_id: rerankCredentialId } : {},
        policies: [{ key: "scope", source_ids: sourceIds, dataset_ids: [],
          ontology_version_id: ontologyId, term_set_version_ids: termSetIds,
          metadata_filters: Object.fromEntries(Object.entries(filters).filter(([, value]) => value.trim())) },
          { key: "ranking", recency_half_life_days: recencyDays,
            metadata_boosts: Object.entries(boostValues).filter(([, value]) => value.trim()).map(([key, value]) => ({
              key, value: value.trim(), weight: boostWeights[key] ?? 1.5,
            })),
            source_trust_weights: Object.fromEntries(Object.entries(sourceTrust).filter(([, value]) => value !== 1)) },
          { key: "acl", enforce: true }] };
      const result = await rawFetch<Profile>(activeId
        ? `/retrieval/profiles/${activeId}/${suffix}` : `/retrieval/profiles/${suffix}`, {
        method: activeId ? "PATCH" : "POST", body: JSON.stringify(payload),
      });
      setActiveId(result.id); onSelect(result.id); await invalidate();
      setNotice(activeId ? "Profile updated." : "Profile created.");
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Could not save profile."); }
    finally { setBusy(false); }
  }

  async function act(action: "clone" | "delete" | "default") {
    if (!workspace || !canEdit) return;
    setBusy(true); setError(""); setNotice("");
    try {
      if (action === "default") {
        const result = await rawFetch<{ profile: Profile }>(`/retrieval/profiles/ensure-default/${suffix}`,
          { method: "POST", body: JSON.stringify({ workspace }) });
        await invalidate(); setActiveId(result.profile.id); onSelect(result.profile.id);
        setNotice("Workspace default is ready.");
      } else if (activeId && action === "clone") {
        const copyName = `${name} copy`;
        const result = await rawFetch<Profile>(`/retrieval/profiles/${activeId}/clone/${suffix}`,
          { method: "POST", body: JSON.stringify({ name: copyName }) });
        await invalidate(); setActiveId(result.id); onSelect(result.id); setNotice("Profile cloned.");
      } else if (activeId && action === "delete" && window.confirm(`Delete ${name}?`)) {
        await rawFetch(`/retrieval/profiles/${activeId}/${suffix}`, { method: "DELETE" });
        await invalidate(); reset(); setNotice("Profile deleted.");
      }
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Action failed."); }
    finally { setBusy(false); }
  }

  return <section className="card mb-5" aria-label="Retrieval profile builder">
    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
      <div><h2 className="text-base font-semibold">Profile builder</h2>
        <p className="text-xs text-muted">Choose indexed evidence and exact ontology pins. ACL is always enforced for profiles saved here.</p></div>
      <div className="flex gap-2">{canEdit && <button type="button" className="btn-secondary" onClick={() => act("default")}
        disabled={busy || !workspace}>Use workspace default</button>}
        <button type="button" className="btn-secondary" onClick={reset}>New profile</button></div>
    </div>
    <div className="grid gap-4 lg:grid-cols-[15rem_1fr]">
      <div className="space-y-1" aria-label="Saved profiles">
        {profiles.data?.results.map((row) => <button type="button" key={row.id}
          className={`block w-full border p-2 text-left text-xs ${row.id === activeId ? "border-accent bg-accent/10" : "border-border"}`}
          onClick={() => { setActiveId(row.id); onSelect(row.id); }}>{row.name}</button>)}
        {!profiles.isLoading && !profiles.data?.results.length && <p className="text-xs text-muted">No profile yet.</p>}
      </div>
      <div className="space-y-4">
        <div className="grid gap-2 sm:grid-cols-2">
          <label className="text-xs">Name<input className="input mt-1 w-full" value={name} disabled={!canEdit}
            onChange={(event) => setName(event.target.value)} /></label>
          <label className="text-xs">Description<input className="input mt-1 w-full" value={description} disabled={!canEdit}
            onChange={(event) => setDescription(event.target.value)} /></label>
          <label className="text-xs">Ontology version<select className="input mt-1 w-full" value={ontologyId} disabled={!canEdit}
            onChange={(event) => setOntologyId(event.target.value)}><option value="">None</option>
            {versions.data?.results.map((version) => <option key={version.id} value={version.id}>{version.semver} · {version.status}</option>)}</select></label>
          <label className="text-xs">Results to return<input type="number" min={1} max={100} className="input mt-1 w-full"
            value={topK} disabled={!canEdit} onChange={(event) => setTopK(Number(event.target.value))} />
            <span className="mt-1 block text-muted">Usually 10 for a person, 5 when bound to an agent. Allowed range: 1–100.</span></label>
        </div>
        <fieldset className="border border-border p-3"><legend className="px-1 text-xs font-semibold">Choose a purpose</legend>
          <p className="mb-2 text-xs text-muted">Start with a preset; only methods supported by this workspace are enabled.</p>
          <div className="grid gap-2 sm:grid-cols-2">{Object.entries(presets).map(([label, keys]) => {
            const supported = keys.filter(available);
            const active = routing === "fixed" && supported.length > 0 && supported.length === selected.length
              && supported.every((key) => selected.includes(key));
            return <button key={label} type="button" aria-pressed={active}
              className={`border p-3 text-left text-xs focus-visible:outline-2 focus-visible:outline-accent ${active ? "border-accent bg-accent/10" : "border-border bg-panel"}`}
              disabled={!canEdit || !supported.length} onClick={() => { setRouting("fixed"); setSelected(supported); }}>
              <span className="flex justify-between gap-2"><strong className="text-sm">{label}</strong>
                {label === recommendedPreset && <span className="pill-good">Recommended</span>}</span>
              <span className="mt-1 block text-muted">{presetDescriptions[label]}</span>
              {!supported.length && <span className="mt-1 block text-warn">Add the content this preset needs first.</span>}
            </button>;
          })}</div>
        </fieldset>
        <TermSetSelector workspace={workspace} selected={termSetIds} onChange={setTermSetIds} disabled={!canEdit} />
        <fieldset className="border border-border p-3"><legend className="px-1 text-xs font-semibold">Sources</legend>
          <div className="grid gap-2 sm:grid-cols-2">{sources.data?.results
            .filter((source) => source.connector_key !== "discovery.paste")
            .map((source) => { const stats = readiness.data?.sources.find((row) => row.id === source.id);
              const checked = sourceIds.includes(source.id);
              return <label key={source.id} className="border border-border p-2 text-xs">
                <span className="flex items-center gap-2"><Checkbox checked={checked}
                  disabled={!canEdit || (!checked && !!activeId && stats?.available === false)}
                  onChange={() => setSourceIds(checked ? sourceIds.filter((id) => id !== source.id) : [...sourceIds, source.id])} />
                  <strong>{source.name}</strong></span>
                {stats ? <span className="mt-1 block text-muted">{stats.documents} documents · {stats.chunks} indexed passages · embeddings {stats.embedded ? "yes" : "no"}
                  <br />Last sync: {stats.last_sync ? new Date(stats.last_sync).toLocaleDateString() : "none"}
                  {stats.metadata_profile && <> · Metadata profile: {stats.metadata_profile}</>}
                  {!stats.available && <span className="block text-warn">{stats.reason}. Index this source in Catalog first.</span>}</span>
                  : <span className="mt-1 block text-muted">Save the profile to check this source’s searchable content.</span>}
              </label>; })}</div>
          <p className="mt-2 text-xs text-muted">None selected means all sources this reader is permitted to search.</p>
          {!!sources.data?.results.length && <div className="mt-3 border-t border-border pt-2">
            <h3 className="text-xs font-semibold">Source trust</h3>
            <p className="mt-1 text-xs text-muted">Normal (1×) is the starting point. High favors a source (1.25×); Low reduces its influence (0.75×). Change this when a source is more or less authoritative.</p>
            <div className="mt-2 grid gap-2 sm:grid-cols-2">{sources.data.results.map((source) => {
              const weight = sourceTrust[source.id] ?? 1;
              const choice = weight === 1.25 ? "high" : weight === 0.75 ? "low" : weight === 1 ? "normal" : "custom";
              return <label key={source.id} className="text-xs">{source.name}
                <select className="input mt-1 w-full" value={choice} disabled={!canEdit}
                  onChange={(event) => setSourceTrust({ ...sourceTrust,
                    [source.id]: event.target.value === "high" ? 1.25 : event.target.value === "low" ? 0.75 : 1 })}>
                  <option value="high">High</option><option value="normal">Normal</option><option value="low">Low</option>
                  {choice === "custom" && <option value="custom">Custom (Advanced)</option>}
                </select>
              </label>;
            })}</div>
          </div>}
        </fieldset>
        <fieldset className="border border-border p-3"><legend className="px-1 text-xs font-semibold">Routing and methods</legend>
          <div className="mb-2 flex gap-4 text-xs"><label><Radio checked={routing === "fixed"} disabled={!canEdit}
            onChange={() => setRouting("fixed")} /> Fixed</label><label><Radio checked={routing === "auto"}
              disabled={!canEdit} onChange={() => setRouting("auto")} /> Auto</label></div>
          <p className="mb-2 text-xs text-muted">Auto looks for an exact approved term first, then relationships or indexed documents. It records why it chose each method.</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-xs">Prefer exact wording ↔ prefer meaning
              <select className="input mt-1 w-full" value={wordingPreference} disabled={!canEdit || routing === "auto"}
                onChange={(event) => setWeights({ ...weights,
                  bm25: event.target.value === "wording" ? 1.5 : event.target.value === "meaning" ? 0.75 : 1,
                  vector: event.target.value === "meaning" ? 1.5 : event.target.value === "wording" ? 0.75 : 1 })}>
                <option value="wording">Prefer exact wording</option><option value="equal">Balance both</option>
                <option value="meaning">Prefer meaning</option></select>
              <span className="mt-1 block text-muted">Only changes ranking when both document methods are selected. Equal weights are the starting point.</span>
            </label>
            <label className="text-xs">Prefer recent documents
              <select className="input mt-1 w-full" value={recencyChoice} disabled={!canEdit}
                onChange={(event) => setRecencyDays(event.target.value === "strongly" ? 30 : event.target.value === "somewhat" ? 180 : 0)}>
                <option value="off">Off</option><option value="somewhat">Somewhat</option>
                <option value="strongly">Strongly</option>{recencyChoice === "custom" && <option value="custom">Custom (Advanced)</option>}
              </select>
              <span className="mt-1 block text-muted">Off is the starting point. Somewhat fades older results over about 180 days; Strongly over about 30 days.</span>
            </label>
          </div>
          <details className="mt-3 border-t border-border pt-2 text-xs"><summary className="cursor-pointer font-medium">Advanced methods and weights</summary>
          <p className="mt-2 text-muted">Weights range from 0 to 10; 1 gives each selected method equal influence. Graph hops are usually 1–2.</p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">{methods.map(({ key, label }) => <label key={key} className="border border-border p-2 text-xs">
            <span><Checkbox checked={selected.includes(key)} disabled={!canEdit || routing === "auto" || !available(key)}
              onChange={() => setSelected(selected.includes(key) ? selected.filter((item) => item !== key) : [...selected, key])} /> {label}</span>
            {!available(key) && <span className="block text-muted">{reason(key)}</span>}
            {selected.includes(key) && routing === "fixed" && <span className="mt-1 block">Weight <DecimalInput
              label={`${label} weight`} value={weights[key]} min={0} max={10} disabled={!canEdit}
              onChange={(value) => setWeights({ ...weights, [key]: value })} /></span>}
            {key === "graph" && selected.includes(key) && <span className="mt-1 block">Relationship steps <input type="number" min={1} max={4}
              className="input ml-1 w-16" value={hops} disabled={!canEdit} onChange={(event) => setHops(Number(event.target.value))} /></span>}
          </label>)}</div></details>
        </fieldset>
        <fieldset className="border border-border p-3"><legend className="px-1 text-xs font-semibold">Metadata filters</legend>
          <p className="mb-2 text-xs text-muted">Narrow document results by context. Choose a suggested value or type one; blank means no filter. Glossary class filtering uses Data entity.</p>
          <div className="grid gap-2 sm:grid-cols-3">{metadataKeys.map((key) => <label key={key} className="text-xs">{key.replaceAll("_", " ")}
            <input className="input mt-1 w-full" list={`retrieval-${key}-options`} value={filters[key] || ""} disabled={!canEdit}
              onChange={(event) => setFilters({ ...filters, [key]: event.target.value })} />
            <datalist id={`retrieval-${key}-options`}>{readiness.data?.filter_options?.[key]?.map((value) =>
              <option key={value} value={value} />)}</datalist></label>)}</div>
          {activeId && <p className={`mt-2 text-xs ${Object.values(filters).some(Boolean) && readiness.data?.documents.documents === 0 ? "text-warn" : "text-muted"}`} role="status">
            {readiness.isFetching ? "Checking matching documents…" : `${readiness.data?.documents.documents ?? 0} matching permitted documents`}
            {Object.values(filters).some(Boolean) && !readiness.isFetching && readiness.data?.documents.documents === 0
              ? " — this filter currently matches nothing." : "."}
          </p>}
        </fieldset>
        <details className="border border-border p-3 text-xs"><summary className="cursor-pointer font-semibold">Ranking: boosts, recency and source trust</summary>
          <label className="mt-3 block">Recency half-life in days (0 = off)
            <input type="number" min={0} max={3650} className="input mt-1 w-32" value={recencyDays} disabled={!canEdit}
              onChange={(event) => setRecencyDays(Number(event.target.value))} /></label>
          <p className="mt-2 text-muted">Boost a matching metadata value after access checks. 1 leaves ranking unchanged; 1.2–1.5 is a typical modest boost (range 0–3).</p>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">{metadataKeys.map((key) => <div key={key} className="grid grid-cols-[1fr_5rem] gap-1">
            <label>{key.replaceAll("_", " ")} value<input className="input mt-1 w-full" value={boostValues[key] || ""} disabled={!canEdit}
              onChange={(event) => setBoostValues({ ...boostValues, [key]: event.target.value })} /></label>
            <label>Weight<DecimalInput label={`${key} boost weight`} value={boostWeights[key] ?? 1.5}
              min={0} max={3} disabled={!canEdit}
              onChange={(value) => setBoostWeights({ ...boostWeights, [key]: value })} /></label>
          </div>)}</div>
          <div className="mt-3"><strong>Source trust weights</strong><div className="mt-1 grid gap-2 sm:grid-cols-2">
            {sources.data?.results.map((source) => <label key={source.id}>{source.name}
              <DecimalInput label={`${source.name} trust weight`} value={sourceTrust[source.id] ?? 1}
                min={0} max={3} disabled={!canEdit}
                onChange={(value) => setSourceTrust({ ...sourceTrust, [source.id]: value })} />
            </label>)}</div></div>
        </details>
        <label className="block text-xs"><Checkbox checked={live} disabled={!canEdit || !caps.data?.["mcp.live"]?.enabled}
          onChange={(event) => setLive(event.target.checked)} /> Include active live sources
          {!caps.data?.["mcp.live"]?.enabled && <span className="ml-2 text-muted">Live connections are not ready for access-controlled search.</span>}</label>
        {live && <label className="block text-xs">MCP source<select className="input ml-2" value={liveSourceId}
          onChange={(event) => setLiveSourceId(event.target.value)} disabled={!canEdit}>
          <option value="">Choose source</option>{sources.data?.results.filter((source) => source.connector_key === "mcp" && source.status === "active")
            .map((source) => <option key={source.id} value={source.id}>{source.name}</option>)}
        </select></label>}
        <label className="block text-xs">LLM reranker (optional)
          <select className="input mt-1 w-full" value={rerankCredentialId} disabled={!canEdit}
            onChange={(event) => setRerankCredentialId(event.target.value)}>
            <option value="">Off · deterministic order</option>
            {llmCredentials.map((credential) => <option key={credential.id} value={credential.id}>
              {credential.label || credential.alias || credential.provider} · {credential.model}</option>)}
          </select>
          <span className="mt-1 block text-muted">Only your selected workspace key is used. Query and up to eight snippets are redacted before egress; unsafe content keeps deterministic order.</span>
          {llmCredentials.length === 0 && <Link to="/settings" className="mt-1 block text-accent underline">Add your LLM key to enable this</Link>}
        </label>
        <div className="flex flex-wrap gap-2">{canEdit && <button type="button" className="btn-primary" onClick={save} disabled={busy || !workspace}>Save profile</button>}
          {canEdit && activeId && <><button type="button" className="btn-secondary" onClick={() => act("clone")} disabled={busy}>Clone</button>
            <button type="button" className="btn-secondary" onClick={() => act("delete")} disabled={busy}>Delete</button></>}</div>
        {notice && <p role="status" className="text-xs text-good">{notice}</p>}
        {error && <p role="alert" className="text-xs text-bad">{error}</p>}
      </div>
    </div>
  </section>;
}
